import React, { useState, useEffect, useContext } from "react";
import { Table, Button, Badge, Modal, Form, Card, Row, Col, Alert, Spinner } from "react-bootstrap";
import axiosClient from "../../config/axiosClient";
import API_ENDPOINTS from "../../config/api"; 
import UserContext from "../../context/UserContext";

const LeaveManagement = () => {
  const { user } = useContext(UserContext); 
  const [requests, setRequests] = useState([]); 
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isViewing, setIsViewing] = useState(false);
  const [submitting, setSubmitting] = useState(false); 
  const [currentId, setCurrentId] = useState(null);
  const [hrHeadId, setHrHeadId] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const [isHalfDay, setIsHalfDay] = useState(false);
  const [newRequest, setNewRequest] = useState({
    leaveType: 0,
    startDate: "",
    endDate: "",
    totalDays: 0,
    reason: "",
    attachmentPath: "" 
  });

  const [serverError, setServerError] = useState(null);
  const [errors, setErrors] = useState({});

  const leaveTypeMap = {
    0: "Annual Leave",
    1: "Sick Leave",
    2: "Unpaid Leave",
    3: "Maternity Leave",
    4: "Wedding Leave",
    5: "Annual Leave (>= 2 days)"
  };

  useEffect(() => {
    fetchMyRequests();
    fetchHRHead();
    const interval = setInterval(fetchMyRequests, 15000); 
    return () => clearInterval(interval);
  }, [user]);

  const fetchHRHead = async () => {
    try {
      const res = await axiosClient.get(`${API_ENDPOINTS.auth}/hr-head`);
      if (res.data?.id) setHrHeadId(res.data.id);
    } catch (err) {
      console.error("HR Head fetch failed:", err);
    }
  };

  const getUserId = () => {
    const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
    return user?.id || user?.userId || storedUser?.id || storedUser?.userId;
  };

  const fetchMyRequests = async () => {
    try {
      const uId = getUserId();
      if (!uId) return;
      const res = await axiosClient.get(API_ENDPOINTS.leaveRequests); 
      const myData = res.data.filter(r => String(r.userId) === String(uId));
      setRequests(myData);
    } catch (err) {
      console.error("Load requests error:", err);
    }
  };

  const calculateTotalDays = (start, end, halfDayActive) => {
    if (!start || !end) return 0;
    if (start === end && halfDayActive) return 0.5;

    let startDate = new Date(start);
    let endDate = new Date(end);
    if (endDate < startDate) return 0;

    let total = 0;
    let curr = new Date(startDate);
    while (curr <= endDate) {
      const dayOfWeek = curr.getDay(); 
      if (dayOfWeek === 0) total += 0; 
      else if (dayOfWeek === 6) total += 0.5; 
      else total += 1;
      curr.setDate(curr.getDate() + 1);
    }
    return total;
  };

  const validate = () => {
    let newErrors = {};
    if (!newRequest.startDate) newErrors.startDate = "Start date is required!";
    if (!newRequest.endDate) newErrors.endDate = "End date is required!";
    if (newRequest.startDate && newRequest.endDate) {
      if (new Date(newRequest.endDate) < new Date(newRequest.startDate)) {
        newErrors.endDate = "End date cannot be earlier than start date!";
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleDateChange = (field, value) => {
    const updatedRequest = { ...newRequest, [field]: value };
    let currentHalfDay = isHalfDay;
    if (updatedRequest.startDate !== updatedRequest.endDate) {
      currentHalfDay = false;
      setIsHalfDay(false);
    }
    const days = calculateTotalDays(updatedRequest.startDate, updatedRequest.endDate, currentHalfDay);
    setNewRequest({ ...updatedRequest, totalDays: days });
    if (errors[field]) setErrors({ ...errors, [field]: null });
    setServerError(null);
  };

  const handleHalfDayToggle = (checked) => {
    setIsHalfDay(checked);
    const days = calculateTotalDays(newRequest.startDate, newRequest.endDate, checked);
    setNewRequest({ ...newRequest, totalDays: days });
  };

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  const handleOpenCreate = () => {
    setIsEditing(false);
    setIsViewing(false);
    setSubmitting(false);
    setIsHalfDay(false);
    setNewRequest({ leaveType: 0, startDate: "", endDate: "", totalDays: 0, reason: "", attachmentPath: "" });
    setSelectedFile(null);
    setErrors({});
    setServerError(null);
    setShowModal(true);
  };

  const handleOpenEdit = (req) => {
    setIsEditing(true);
    setIsViewing(false);
    setSubmitting(false);
    setCurrentId(req.id);
    setIsHalfDay(req.totalDays === 0.5);
    setNewRequest({
      leaveType: req.type === 5 ? 0 : req.type,
      startDate: req.startDate.split('T')[0],
      endDate: req.endDate.split('T')[0],
      totalDays: req.totalDays,
      reason: req.reason,
      attachmentPath: req.attachmentPath 
    });
    setSelectedFile(null);
    setErrors({});
    setServerError(null);
    setShowModal(true);
  };

  const handleView = (req) => {
    setIsViewing(true);
    setIsEditing(false);
    setSubmitting(false);
    setCurrentId(req.id);
    setIsHalfDay(req.totalDays === 0.5);
    setNewRequest({
      leaveType: req.type,
      startDate: req.startDate.split('T')[0],
      endDate: req.endDate.split('T')[0],
      totalDays: req.totalDays,
      reason: req.reason,
      attachmentPath: req.attachmentPath 
    });
    setSelectedFile(null);
    setServerError(null);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this leave request?")) {
      try {
        await axiosClient.delete(`${API_ENDPOINTS.leaveRequests}/${id}`);
        fetchMyRequests();
      } catch (err) { 
        console.error("Delete error:", err);
      }
    }
  };

  const getEffectiveLeaveType = (type, totalDays) => {
    if (type === 0 && totalDays >= 2) return 5; 
    return type;
  };

  const handleSubmit = async () => {
    if (!validate()) return; 
    setServerError(null);
    setSubmitting(true);

    const uId = getUserId();
    const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
    const mId = user?.managerId || storedUser?.managerId || user?.ManagerId || storedUser?.ManagerId; 
    const hId = hrHeadId ? parseInt(hrHeadId) : 1; 

    const formData = new FormData();
    formData.append("Id", isEditing ? currentId : 0);
    formData.append("UserId", uId);
    formData.append("ManagerId", mId || "");
    formData.append("HrId", hId);
    formData.append("Type", getEffectiveLeaveType(parseInt(newRequest.leaveType), parseFloat(newRequest.totalDays)));
    formData.append("StartDate", newRequest.startDate);
    formData.append("EndDate", newRequest.endDate);
    formData.append("TotalDays", parseFloat(newRequest.totalDays));
    formData.append("Reason", newRequest.reason || "Leave Request");
    
    if (selectedFile) {
      formData.append("attachment", selectedFile);
    }

    try {
      const config = { headers: { 'Content-Type': 'multipart/form-data' } };
      if (isEditing) {
        await axiosClient.put(`${API_ENDPOINTS.leaveRequests}/${currentId}`, formData, config);
      } else {
        await axiosClient.post(API_ENDPOINTS.leaveRequests, formData, config);
      }
      setShowModal(false);
      fetchMyRequests();
    } catch (err) { 
      const errorMessage = err.response?.data?.message || err.response?.data || "An unexpected error occurred!";
      setServerError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const renderStatus = (status) => {
    const badges = {
      [-1]: <Badge bg="danger" className="fw-normal rounded-pill px-3">MANAGER REJECTED</Badge>,
      [0]: <Badge bg="warning" text="dark" className="fw-normal rounded-pill px-3">PENDING MANAGER</Badge>,
      [1]: <Badge bg="success" className="fw-normal rounded-pill px-3">MANAGER APPROVED</Badge>,
      [2]: <Badge bg="danger" className="fw-normal rounded-pill px-3">HR REJECTED</Badge>,
      [3]: <Badge bg="info" text="white" className="fw-normal rounded-pill px-3">PENDING HR</Badge>,
      [4]: <Badge bg="success" className="fw-normal rounded-pill px-3">HR APPROVED</Badge>,
      [5]: <Badge bg="danger" className="fw-normal rounded-pill px-3">BLOCK REJECTED</Badge>,
      [6]: <Badge bg="primary" className="fw-normal rounded-pill px-3">PENDING BLOCK MANAGER</Badge>,
      [7]: <Badge bg="success" className="fw-normal rounded-pill px-3">BLOCK APPROVED</Badge>,
      [8]: <Badge bg="danger" className="fw-normal rounded-pill px-3">BOARD REJECTED</Badge>,
      [9]: <Badge bg="secondary" className="fw-normal rounded-pill px-3">PENDING BOARD</Badge>,
      [11]: <Badge bg="success" className="fw-normal rounded-pill px-3">FULLY APPROVED</Badge>,
    };
    return badges[status] || <Badge bg="secondary" className="fw-normal rounded-pill px-3">UNKNOWN</Badge>;
  };

  const getFileName = (path) => {
    if (!path) return "";
    return path.split('/').pop().split('\\').pop();
  };

  return (
    <div className="container-fluid py-4" style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 className="fw-bold text-dark mb-0 text-uppercase">Leave Management</h3>
          <p className="text-muted small mb-0">My Leave Applications</p>
        </div>
        <Button variant="primary" className="px-4 shadow-sm fw-bold rounded-pill" onClick={handleOpenCreate}>
          <i className="fas fa-plus me-2"></i>CREATE NEW
        </Button>
      </div>

      <Card className="shadow-sm border-0 rounded-3">
        <Card.Body className="p-0">
          <Table hover responsive className="mb-0 align-middle">
            <thead className="bg-light">
              <tr>
                <th className="ps-4 py-3 text-muted fw-bold small text-uppercase">Leave Type</th>
                <th className="py-3 text-muted fw-bold small text-uppercase">Duration</th>
                <th className="py-3 text-muted fw-bold small text-center text-uppercase">Days</th>
                <th className="py-3 text-muted fw-bold small text-uppercase">Status</th>
                <th className="py-3 text-muted fw-bold small text-uppercase">Current Approver</th>
                <th className="py-3 text-muted fw-bold small text-end pe-4 text-uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {requests.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((r) => (
                <tr key={r.id} className="border-bottom">
                  <td className="ps-4 py-3">
                    <div className="fw-bold text-dark">{leaveTypeMap[r.type] || "Other"}</div>
                    <div className="text-muted small text-truncate" style={{maxWidth: '200px'}}>{r.reason}</div>
                  </td>
                  <td>
                    <div className="small fw-bold text-secondary">
                      {new Date(r.startDate).toLocaleDateString("en-GB")} → {new Date(r.endDate).toLocaleDateString("en-GB")}
                    </div>
                  </td>
                  <td className="text-center">
                    <span className="badge rounded-pill bg-light text-dark border px-3">{r.totalDays}</span>
                  </td>
                  <td>{renderStatus(r.status)}</td>
                  <td>
                    {r.status === 11 ? (
                      <span className="text-success small fw-bold"><i className="fas fa-check-double me-1"></i>Finalized</span>
                    ) : [-1, -2, -5, -8].includes(r.status) ? (
                      <span className="text-danger small fw-bold"><i className="fas fa-times-circle me-1"></i>Terminated</span>
                    ) : (
                      <div className="d-flex align-items-center">
                        <div className="bg-soft-primary rounded-circle d-flex align-items-center justify-content-center me-2" style={{width: '24px', height: '24px', backgroundColor: '#e7f1ff', color: '#0d6efd'}}>
                           <i className="fas fa-user small"></i>
                        </div>
                        <span className="small fw-bold text-dark">
                          {r.currentApproverName || "Waiting..."}
                        </span>
                      </div>
                    )}
                  </td>
                  <td className="text-end pe-4">
                    <div className="d-flex justify-content-end gap-2">
                      <Button variant="white" className="rounded-circle shadow-sm border text-info" style={{ width: "35px", height: "35px" }} onClick={() => handleView(r)}>
                        <i className="fas fa-eye"></i>
                      </Button>
                      {[0, 3, 6, 9].includes(r.status) && (
                        <>
                          <Button variant="white" className="rounded-circle shadow-sm border text-primary" style={{ width: "35px", height: "35px" }} onClick={() => handleOpenEdit(r)}>
                            <i className="fas fa-pencil-alt"></i>
                          </Button>
                          <Button variant="white" className="rounded-circle shadow-sm border text-danger" style={{ width: "35px", height: "35px" }} onClick={() => handleDelete(r.id)}>
                            <i className="fas fa-trash-alt"></i>
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={() => !submitting && setShowModal(false)} centered size="lg">
        <Modal.Header closeButton={!submitting} className="bg-primary text-white border-0">
          <Modal.Title className="fw-bold fs-5 text-uppercase">
            {isViewing ? "View Request" : isEditing ? "Update Request" : "New Request"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {serverError && (
            <Alert variant="danger" className="mb-4 border-0 shadow-sm small py-2 px-3 fw-bold">
              <i className="fas fa-exclamation-circle me-2"></i>{serverError}
            </Alert>
          )}

          <Form>
            <Row className="mb-4">
              <Col md={6}>
                <Form.Label className="small fw-bold text-muted">START DATE *</Form.Label>
                <Form.Control type="date" isInvalid={!!errors.startDate} value={newRequest.startDate} onChange={(e) => handleDateChange("startDate", e.target.value)} readOnly={isViewing || submitting} />
                <Form.Control.Feedback type="invalid">{errors.startDate}</Form.Control.Feedback>
              </Col>
              <Col md={6}>
                <Form.Label className="small fw-bold text-muted">END DATE *</Form.Label>
                <Form.Control type="date" isInvalid={!!errors.endDate} value={newRequest.endDate} onChange={(e) => handleDateChange("endDate", e.target.value)} readOnly={isViewing || submitting} />
                <Form.Control.Feedback type="invalid">{errors.endDate}</Form.Control.Feedback>
              </Col>
            </Row>

            {newRequest.startDate && newRequest.startDate === newRequest.endDate && (
              <Form.Group className="mb-4">
                <Form.Check 
                  type="switch"
                  id="half-day-switch"
                  label={<span className="fw-bold text-primary small">TAKE HALF DAY OFF (0.5 DAY)</span>}
                  checked={isHalfDay}
                  onChange={(e) => handleHalfDayToggle(e.target.checked)}
                  disabled={isViewing || submitting}
                />
              </Form.Group>
            )}

            <Row className="mb-4">
              <Col md={6}>
                <Form.Label className="small fw-bold text-muted">CATEGORY</Form.Label>
                <Form.Select value={newRequest.leaveType} onChange={(e) => setNewRequest({ ...newRequest, leaveType: parseInt(e.target.value) })} disabled={isViewing || submitting}>
                  <option value={0}>Annual Leave</option>
                  <option value={1}>Sick Leave</option>
                  <option value={2}>Unpaid Leave</option>
                  <option value={3}>Maternity Leave</option>
                  <option value={4}>Wedding Leave</option>
                </Form.Select>
              </Col>
              <Col md={6}>
                <Form.Label className="small fw-bold text-muted">TOTAL DAYS</Form.Label>
                <Form.Control type="text" value={newRequest.totalDays} readOnly className="bg-light fw-bold text-primary" />
              </Col>
            </Row>

            <Form.Group className="mb-4">
              <Form.Label className="small fw-bold text-muted">ATTACHMENT (IMAGE/PDF)</Form.Label>
              <div className="mt-1">
                {!isViewing ? (
                  <div className="position-relative">
                    <Form.Control 
                      type="file" 
                      onChange={handleFileChange} 
                      accept="image/*,application/pdf" 
                      disabled={submitting}
                    />
                    {isEditing && newRequest.attachmentPath && !selectedFile && (
                      <div 
                        className="position-absolute bg-white" 
                        style={{ 
                          top: "2px", left: "100px", right: "2px", bottom: "2px", 
                          display: "flex", alignItems: "center", paddingLeft: "10px",
                          pointerEvents: "none", fontSize: "0.9rem", color: "#495057"
                        }}
                      >
                        <span className="text-truncate" style={{maxWidth: "80%"}}>
                          {getFileName(newRequest.attachmentPath)}
                        </span>
                      </div>
                    )}
                  </div>
                ) : null}

                {newRequest.attachmentPath ? (
                  <div className={!isViewing ? "mt-3 d-flex gap-2" : "d-flex gap-2"}>
                    <a 
                      href={newRequest.attachmentPath} 
                      target="_blank" 
                      rel="noreferrer" 
                      className="btn btn-outline-primary btn-sm rounded-pill px-3 shadow-sm"
                    >
                      <i className="fas fa-eye me-2"></i>PREVIEW
                    </a>
                    <a 
                      href={newRequest.attachmentPath} 
                      download={getFileName(newRequest.attachmentPath)}
                      className="btn btn-outline-success btn-sm rounded-pill px-3 shadow-sm"
                    >
                      <i className="fas fa-download me-2"></i>DOWNLOAD
                    </a>
                  </div>
                ) : isViewing ? (
                  <span className="text-muted italic small">No attachment provided</span>
                ) : null}
              </div>
            </Form.Group>

            <Form.Group>
              <Form.Label className="small fw-bold text-muted">REASON</Form.Label>
              <Form.Control as="textarea" rows={3} value={newRequest.reason} onChange={(e) => setNewRequest({...newRequest, reason: e.target.value})} readOnly={isViewing || submitting} />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0 p-4">
          <Button variant="link" className="text-secondary fw-bold text-decoration-none" onClick={() => setShowModal(false)} disabled={submitting}>
            CLOSE
          </Button>
          {!isViewing && (
            <Button variant="primary" className="px-5 fw-bold shadow-sm rounded-pill" onClick={handleSubmit} disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                  PROCESSING...
                </>
              ) : (
                isEditing ? "SAVE CHANGES" : "SUBMIT REQUEST"
              )}
            </Button>
          )}
        </Modal.Footer> 
      </Modal>
    </div>
  );
};

export default LeaveManagement;